import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, saveJwt } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getStoredRef, clearStoredRef } from "@/lib/refCode";
import { toast } from "sonner";

/**
 * Handles the redirect from Emergent-managed Google OAuth.
 * Flow: Emergent redirects to /auth/callback#session_id=<token>.
 * We POST that session_id to our backend which exchanges it for a
 * long-lived session cookie, then we navigate to /dashboard.
 *
 * Double-fire protection: React StrictMode (dev) + fast navigation can
 * re-run the effect. The OAuth session_id is single-use: a second POST
 * returns 401, which would show a spurious "Accesso Google fallito" toast
 * even though the first POST succeeded and the user is already logged in.
 * We guard with sessionStorage (persists across remounts of this component)
 * and clean the URL hash immediately so nothing else can re-read it.
 */
const PROCESSED_KEY = "pronto_auth_session_processed";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // iter v29 r191 — Se siamo dentro l'app iOS native, chiudi la
    // SFSafariViewController che ha portato qui il session_id (no-op su web).
    (async () => {
      try {
        const { nativeCloseBrowser } = await import("@/lib/native");
        await nativeCloseBrowser();
      } catch (_) { /* no-op */ }
    })();

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;

    if (!sessionId) {
      // No session in hash → either user hit /auth/callback directly,
      // or we already processed this callback in a previous mount.
      // Silent redirect, no error toast.
      navigate("/login", { replace: true });
      return;
    }

    // Guard against double-processing the same single-use session_id.
    // sessionStorage survives StrictMode remounts and fast re-renders.
    const alreadyProcessed = sessionStorage.getItem(PROCESSED_KEY);
    if (alreadyProcessed === sessionId) {
      // Clean & bounce to dashboard assuming first call succeeded.
      window.history.replaceState(null, "", window.location.pathname);
      navigate("/dashboard", { replace: true });
      return;
    }
    sessionStorage.setItem(PROCESSED_KEY, sessionId);

    // Strip the hash IMMEDIATELY so any late-running effect can't see it.
    window.history.replaceState(null, "", window.location.pathname);

    (async () => {
      try {
        const refCode = getStoredRef();
        const headers = { "X-Session-ID": sessionId };
        if (refCode) headers["X-Referral-Code"] = refCode;
        const { data } = await api.post("/auth/session", null, { headers });
        // iter 251 fix Safari ITP: salviamo il session_token in cookie+localStorage
        // (dual-storage tramite saveJwt) così se Safari cancella il cookie httpOnly
        // del backend, l'utente resta loggato grazie all'Authorization header.
        // iter 313 r24: saveJwt import diretto (era dynamic) — su mobile lento
        // l'await import() poteva ritardare il salvataggio del token e causare
        // race condition con il navigate successivo (→ ProtectedRoute vedeva
        // ancora user=null → redirect a /login → loop infinito Google).
        if (data?.token) {
          saveJwt(data.token);
        }
        setUser(data.user);
        if (refCode) clearStoredRef();
        // iter 313 r24: window.location.replace() invece di navigate() su
        // mobile — un full reload garantisce che AuthProvider riparta da zero,
        // legga il token appena salvato dal localStorage e popoli `user`
        // CORRETTAMENTE prima che ProtectedRoute renderizzi. Con navigate()
        // c'era una race condition state-async su iOS Safari mobile dove il
        // ProtectedRoute leggeva user=null e cacciava a /login → loop OAuth.
        window.location.replace("/dashboard");
      } catch (e) {
        // If the failure is 401 AND we know we already processed this
        // session_id elsewhere, stay silent (the other call succeeded).
        // Otherwise this is a real auth failure.
        const status = e?.response?.status;
        if (status === 401 && sessionStorage.getItem(PROCESSED_KEY) === sessionId) {
          // Race con sibling mount; vai al dashboard, /me risolverà lo stato.
          window.location.replace("/dashboard");
          return;
        }
        sessionStorage.removeItem(PROCESSED_KEY);
        toast.error("Accesso Google fallito");
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFFFF]" data-testid="auth-callback-screen">
      <div className="font-editorial text-3xl tracking-tight text-[#7C8B6E] vista-pulse">Autenticazione in corso…</div>
    </div>
  );
}
